import React from "react";
import { I18n } from "@aws-amplify/core";
import { withStyles } from "@material-ui/styles";
import { get, map, max, min, filter } from "lodash";

import {
  Map,
  CircleMarker,
  TileLayer,
  Tooltip,
  ZoomControl,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import Grid from "@material-ui/core/Grid";
import Radio from "@material-ui/core/Radio";

import DashboardCard from "../DashboardCard";
import {
  sizes,
  activeCases,
  failedSurveys,
  feverdetected,
} from "../../../constants/common";
import colors from "../../../assets/style/colors";

const center = {
  lat: 45.950401,
  long: -75.688803,
};

const styles = {
  active: {
    color: colors.primary,
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  title: {
    color: colors.black87,
    fontWeight: 500,
    fontSize: "1.125em",
  },
  map: {
    "& .leaflet-tile-pane": {
      webkitFilter: "grayscale(100%)",
      filter: "grayscale(100%)",
    },
  },
  activeCasesColor: {
    color: `${colors.redCoral} !important`,
  },
  failedSurveysColor: {
    color: `${colors.yellowDoughnut} !important`,
  },
  feverdetectedColor: {
    color: `${colors.bodyPink} !important`,
  },
};

const HspMap = (props) => {
  const { classes, data } = props;
  const [displayedData, setDisplayedData] = React.useState(failedSurveys);

  const maxLat = max(map(data, (d) => get(d, ["coordinates", 0])));
  const minLat = min(map(data, (d) => get(d, ["coordinates", 0])));
  const maxLong = max(map(data, (d) => get(d, ["coordinates", 1])));
  const minLong = min(map(data, (d) => get(d, ["coordinates", 1])));

  const bufferLat = (maxLat - minLat) * 0.05;
  const bufferLong = (maxLong - minLong) * 0.15;

  return (
    <DashboardCard size={sizes.medium}>
      <Grid container direction="column" style={{ marginTop: "-10px" }}>
        <Grid item>
          <Grid
            container
            alignItems="center"
            justify="center"
            style={{ paddingTop: "8px" }}
          >
            {map([activeCases, failedSurveys, feverdetected], (type) => (
              <Grid item key={type} style={{ padding: "0px 15px" }}>
                <Radio
                  checked={displayedData === type}
                  onChange={() =>
                    displayedData === type ? "" : setDisplayedData(type)
                  }
                  classes={{
                    colorSecondary: classes[`${type}Color`],
                  }}
                />
                <span
                  onClick={() =>
                    displayedData === type ? "" : setDisplayedData(type)
                  }
                  style={{ cursor: "pointer" }}
                >
                  {I18n.get(`dashboard_${type}`)}
                </span>
              </Grid>
            ))}
          </Grid>
        </Grid>
        <Grid item xs style={{ padding: "8px", paddingTop: "6px" }}>
          <Map
            style={{ height: "390px", width: "100%", zIndex: 1 }}
            zoom={1}
            zoomControl={false}
            center={[center.lat, center.long]}
            bounds={
              data && !isNaN(bufferLat) && !isNaN(bufferLong)
                ? [
                  [minLat - bufferLat, minLong - bufferLong],
                  [maxLat + bufferLat, maxLong + bufferLong],
                ]
                : null
            }
            className={classes.map}
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {map(
              filter(
                data,
                (site) =>
                  get(site, ["coordinates", 0]) && get(site, ["coordinates", 1])
              ),
              (site, index) => (
                <CircleMarker
                  key={index}
                  center={[
                    get(site, ["coordinates", 0]),
                    get(site, ["coordinates", 1]),
                  ]}
                  radius={max([
                    20 *
                    Math.log(
                      get(site, [displayedData]) /
                      (displayedData === activeCases ? 10 : 5)
                    ),
                    10,
                  ])}
                  fillOpacity={0.9}
                  fillColor={
                    get(site, [displayedData]) === 0
                      ? colors.cardGreen
                      : displayedData === activeCases
                        ? colors.redCoral
                        : displayedData === failedSurveys
                          ? colors.yellowDoughnut
                          : colors.bodyPink
                  }
                  stroke={false}
                >
                  <Tooltip direction="top" offset={[0, 0]} opacity={1}>
                    <span className="text-center bold-500">
                      {get(site, ["name"])}
                    </span>
                    <br />
                    {displayedData === activeCases && (
                      <>
                        <span>
                          {I18n.get("dashboard_activeCases")}
                          {": "}
                        </span>
                        <span className="bold-500">
                          {get(site, [activeCases])}
                        </span>
                      </>
                    )}
                    {displayedData === failedSurveys && (
                      <>
                        <span>
                          {I18n.get("dashboard_failedSurveys")}
                          {": "}
                        </span>
                        <span className="bold-500">
                          {get(site, [failedSurveys])}
                        </span>
                      </>
                    )}
                    {displayedData === feverdetected && (
                      <>
                        <span>
                          {I18n.get("dashboard_feverdetected")}
                          {": "}
                        </span>
                        <span className="bold-500">
                          {get(site, [feverdetected])}
                        </span>
                      </>
                    )}
                  </Tooltip>
                </CircleMarker>
              )
            )}
            <ZoomControl position="bottomright" />
          </Map>
        </Grid>
      </Grid>
    </DashboardCard>
  );
};

export default withStyles(styles)(HspMap);

// https://towardsdatascience.com/creating-a-bubbles-map-using-react-leaflet-e75124ca1cd2
